import React, { useEffect, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CompanySettings } from '@domain/account/models/Company';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import CreateShipmentMethodDialog from '@features/shipments/components/methods/CreateShipmentMethodDialog';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import { APP_CONFIG } from '../../../../config/app.config';
import { useToast } from '@shared/hooks/useToast';
import OnboardingOverlayCard from '../../shared/OnboardingOverlayCard';
import OnboardingSkipFooter from '../../shared/OnboardingSkipFooter';
import SubstepCard, { type SubstepStatus } from './SubstepCard';
import {
  CatalogSubstep,
  daysToWeeks,
  fieldSx,
  formatDays,
  isSubstepComplete,
  weeksToDays,
} from './defaults';

const CREATE_NEW_SHIPMENT = '__create_new__';

interface ProductCatalogSetupViewProps {
  settings?: CompanySettings;
  activeSubstep: CatalogSubstep | null;
  submitting: CatalogSubstep | null;
  isImportingProducts: boolean;
  onDismiss: () => void;
  onActivate: (id: CatalogSubstep) => void;
  onCancel: () => void;
  onSave: (
    substep: CatalogSubstep,
    settingsPatch: Partial<CompanySettings>,
    productUpdate: Record<string, unknown>,
  ) => Promise<void>;
}

const ProductCatalogSetupView: React.FC<ProductCatalogSetupViewProps> = ({
  settings,
  activeSubstep,
  submitting,
  isImportingProducts,
  onDismiss,
  onActivate,
  onCancel,
  onSave,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const { error: showError } = useToast();
  const { shipmentMethods } = useShipmentMethods();

  const [productionDays, setProductionDays] = useState('');
  const [processingDays, setProcessingDays] = useState('');
  const [shipmentMethod, setShipmentMethod] = useState<ShipmentMethod | undefined>();
  const [createShipmentOpen, setCreateShipmentOpen] = useState(false);
  const [bufferWeeks, setBufferWeeks] = useState('');
  const [orderWeeks, setOrderWeeks] = useState('');

  useEffect(() => {
    if (settings?.defaultProductionDuration != null) {
      setProductionDays(String(settings.defaultProductionDuration));
    }
    if (settings?.defaultProcessingDuration != null) {
      setProcessingDays(String(settings.defaultProcessingDuration));
    }
    if (settings?.defaultMinimumDaysOfStockInReserve != null) {
      setBufferWeeks(
        daysToWeeks(settings.defaultMinimumDaysOfStockInReserve) ?? '',
      );
    }
    if (settings?.defaultMinimumOrderAmountDays != null) {
      setOrderWeeks(
        daysToWeeks(settings.defaultMinimumOrderAmountDays) ?? '',
      );
    }
  }, [settings]);

  useEffect(() => {
    if (!settings?.defaultShipmentMethodId || !shipmentMethods?.length) return;
    const method = shipmentMethods.find((m) => m.id === settings.defaultShipmentMethodId);
    if (method) setShipmentMethod(method);
  }, [settings?.defaultShipmentMethodId, shipmentMethods]);

  const statusFor = (id: CatalogSubstep): SubstepStatus => {
    if (activeSubstep === id) return 'active';
    if (isSubstepComplete(id, settings)) return 'finished';
    return 'inactive';
  };

  const shippingDays = shipmentMethod?.duration ?? 0;
  const totalDays =
    (Number(productionDays) || 0) + shippingDays + (Number(processingDays) || 0);
  const leadTimeBarSegments = [
    { value: Number(productionDays) || 0, color: colors.primary },
    { value: shippingDays, color: colors.secondary },
    { value: Number(processingDays) || 0, color: colors.success },
  ];

  const methodLabel = (method: ShipmentMethod) =>
    `${method.name || method.transportMethod} · ${formatDays(method.duration)}`;

  const saveLeadTime = async () => {
    const production = Number(productionDays);
    const processing = Number(processingDays);
    if (
      Number.isNaN(production) ||
      production < 0 ||
      Number.isNaN(processing) ||
      processing < 0
    ) {
      showError('Please enter valid day values');
      return;
    }
    await onSave(
      'leadTime',
      {
        defaultProductionDuration: production,
        defaultProcessingDuration: processing,
        ...(shipmentMethod?.id ? { defaultShipmentMethodId: shipmentMethod.id } : {}),
      },
      {
        productionDuration: production,
        processingDuration: processing,
        ...(shipmentMethod?.id ? { defaultShipmentMethodId: shipmentMethod.id } : {}),
      },
    );
  };

  const saveWeeks = async (
    substep: 'buffer' | 'orderQuantity',
    weeks: string,
    settingsKey: keyof CompanySettings,
    productKey: string,
  ) => {
    const days = weeksToDays(weeks);
    if (Number.isNaN(days) || days < 0) {
      showError('Please enter a valid number of weeks');
      return;
    }
    await onSave(substep, { [settingsKey]: days }, { [productKey]: days });
  };

  return (
    <>
      <OnboardingOverlayCard
        title="Set up your product catalog"
        footer={<OnboardingSkipFooter onDismiss={onDismiss} />}
      >
        <Typography variant="body1" color="text.secondary">
          Easily set the default values for your products. You can always change this per product
          later.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SubstepCard
            onCancel={onCancel}
            status={statusFor('leadTime')}
            stepNumber={1}
            title="Lead time"
            description="Lead time consists of 3 parts: production time, shipping time, and processing time. Together, this is the total time from order until it is in stock."
            hideAction={isImportingProducts}
            isSubmitting={submitting === 'leadTime'}
            onActivate={() => onActivate('leadTime')}
            onSubmit={() => void saveLeadTime()}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <FieldRow label="Production">
                <DaysField value={productionDays} onChange={setProductionDays} />
              </FieldRow>

              <FieldRow label="Shipping method">
                <FormControl sx={{ minWidth: 120, maxWidth: 280 }}>
                  <Select
                    displayEmpty
                    value={shipmentMethod?.id ?? ''}
                    onChange={(e) => {
                      const value = e.target.value as string;
                      if (value === CREATE_NEW_SHIPMENT) {
                        setCreateShipmentOpen(true);
                        return;
                      }
                      setShipmentMethod(shipmentMethods?.find((m) => m.id === value));
                    }}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Choose a shipping method
                          </Typography>
                        );
                      }
                      const method = shipmentMethods?.find((m) => m.id === value);
                      return method ? methodLabel(method) : 'Choose a shipping method';
                    }}
                    sx={{ borderRadius: '6px', bgcolor: '#FAFDFF', height: 40 }}
                  >
                    {(shipmentMethods?.length ?? 0) === 0 && (
                      <MenuItem disabled value="">
                        No shipping methods yet
                      </MenuItem>
                    )}
                    {shipmentMethods?.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {methodLabel(method)}
                      </MenuItem>
                    ))}
                    <MenuItem value={CREATE_NEW_SHIPMENT}>
                      <AddIcon fontSize="small" sx={{ mr: 1 }} />
                      Create new
                    </MenuItem>
                  </Select>
                </FormControl>
              </FieldRow>

              <FieldRow label="Processing">
                <DaysField value={processingDays} onChange={setProcessingDays} />
              </FieldRow>

              <Divider sx={{ borderColor: colors.border }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    Total
                  </Typography>
                  <Typography variant="subtitle2" sx={{ flex: 1, textAlign: 'right' }}>
                    {formatDays(totalDays)}
                  </Typography>
                </Box>
                {totalDays > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '2px',
                      height: 12,
                      borderRadius: '16px',
                      overflow: 'hidden',
                      width: '100%',
                    }}
                  >
                    {leadTimeBarSegments.map((segment, index) =>
                      segment.value > 0 ? (
                        <Box
                          key={index}
                          sx={{
                            bgcolor: segment.color,
                            width: `${(segment.value / totalDays) * 100}%`,
                            borderRadius: '12px',
                            border: `1px solid ${colors.background}`,
                            minWidth: 8,
                          }}
                        />
                      ) : null,
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </SubstepCard>

          <SubstepCard
            onCancel={onCancel}
            status={statusFor('buffer')}
            stepNumber={2}
            title="Safety stock"
            description="Safety stock prevents you from running out of stock in case of delays. We recommend keeping an extra 2 to 4 weeks of stock."
            hideAction={isImportingProducts}
            isSubmitting={submitting === 'buffer'}
            onActivate={() => onActivate('buffer')}
            onSubmit={() =>
              void saveWeeks(
                'buffer',
                bufferWeeks,
                'defaultMinimumDaysOfStockInReserve',
                'minimumDaysOfStockInReserve',
              )
            }
          >
            <WeeksField
              label="How many weeks of safety stock do you want to maintain?"
              value={bufferWeeks}
              onChange={setBufferWeeks}
            />
          </SubstepCard>

          <SubstepCard
            onCancel={onCancel}
            status={statusFor('orderQuantity')}
            stepNumber={3}
            title="Order quantity"
            description="For how many weeks ahead do you want to order by default? Based on that, we automatically calculate the order quantities."
            hideAction={isImportingProducts}
            isSubmitting={submitting === 'orderQuantity'}
            onActivate={() => onActivate('orderQuantity')}
            onSubmit={() =>
              void saveWeeks(
                'orderQuantity',
                orderWeeks,
                'defaultMinimumOrderAmountDays',
                'minimumOrderAmountDays',
              )
            }
          >
            <WeeksField
              label="How many weeks ahead do you want to order by default?"
              value={orderWeeks}
              onChange={setOrderWeeks}
            />
          </SubstepCard>
        </Box>
      </OnboardingOverlayCard>

      <CreateShipmentMethodDialog
        open={createShipmentOpen}
        onClose={() => setCreateShipmentOpen(false)}
        onSuccess={(method) => {
          setShipmentMethod(method);
          setCreateShipmentOpen(false);
        }}
      />
    </>
  );
};

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Typography variant="body2" color="text.primary" sx={{ flex: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function DaysField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      placeholder="0"
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
      InputProps={{
        endAdornment: <InputAdornment position="end">days</InputAdornment>,
      }}
      inputProps={{ style: { textAlign: 'right' } }}
      sx={fieldSx}
    />
  );
}

function WeeksField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {label}
      </Typography>
      <TextField
        placeholder="0"
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
        InputProps={{
          endAdornment: <InputAdornment position="end">weeks</InputAdornment>,
        }}
        inputProps={{ style: { textAlign: 'right' } }}
        sx={fieldSx}
      />
    </Box>
  );
}

export default ProductCatalogSetupView;
